import { COUNTRY_CODES } from '@wix/form-multiline-address';
import { generateUUID } from '@wix/form-schema';
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ComboBox as ComboBoxRAC,
  Input as InputRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
  TextField as TextFieldRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { type MlaPostalCodeProps } from '../../../fields/mla-postal-code/mla-postal-code/mla-postal-code-props';
import { useAddressAutofill } from '../../../fields/multiline-address/multiline-address/address-autofill-context';
import { useCountryCode } from '../../../fields/multiline-address/multiline-address/country-code-context';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  applyInternalProps,
  findChildOfType,
} from '../../../services/find-child';
import type { CommonAddress } from '../../address-input/atlas-service';
import { useJapanAutocomplete } from '../../address-input/use-japan-autocomplete';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

export type PostalCodeProps = {
  children: ReactNode;
};

export const PostalCode = ({ children }: PostalCodeProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
    autocomplete,
  } = useFieldPropsV2<MlaPostalCodeProps>();
  const countryCode = useCountryCode();
  const isJapan = Boolean(
    countryCode && COUNTRY_CODES[countryCode] === COUNTRY_CODES.JP,
  );
  const isAutocomplete = autocomplete && isJapan;

  const postalCodeLabel = useAddressLabel('zipCode');
  const labelElement = findChildOfType(children, PostalCode.Label);
  const inputElement = findChildOfType(children, PostalCode.Input);
  const optionsElement = findChildOfType(children, PostalCode.Options);
  const descriptionElement = findChildOfType(children, PostalCode.Description);
  const errorElement = findChildOfType(children, PostalCode.Error);

  if (isAutocomplete) {
    return (
      <FieldLayout
        fieldId={id}
        renderLabel={() =>
          applyInternalProps<LabelInternalProps>(labelElement, {
            label: postalCodeLabel,
          })
        }
        renderInput={() => (
          <PostalCodeAutocomplete
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            hasError={hasError}
            onBlur={onBlur}
            onFocus={onFocus}
          >
            {inputElement}
            {optionsElement}
          </PostalCodeAutocomplete>
        )}
        renderDescription={() => (
          <>
            {descriptionElement}
            {errorElement}
          </>
        )}
      />
    );
  }

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: postalCodeLabel,
        })
      }
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type PostalCodeAutocompleteProps = {
  children: ReactNode;
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  hasError?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
};

const PostalCodeAutocomplete = ({
  children,
  value,
  onChange,
  disabled,
  required,
  hasError,
  onBlur,
  onFocus,
}: PostalCodeAutocompleteProps) => {
  const { onAddressSelect } = useAddressAutofill();
  const [isAutocompleteEnabled, setIsAutocompleteEnabled] = useState(false);
  const { searchByPostalCode } = useJapanAutocomplete({
    isAutocompleteEnabled,
  });
  const [prediction, setPrediction] = useState<CommonAddress>();

  const options = useMemo(
    () =>
      prediction
        ? [
            {
              id: generateUUID(),
              label: prediction.formattedAddress || '',
            },
          ]
        : [],
    [prediction],
  );

  const handleInputChange = useCallback(
    async (newValue: string) => {
      onChange(newValue);
      const address = await searchByPostalCode(newValue ?? '');
      setPrediction(address);
    },
    [onChange, searchByPostalCode],
  );

  const handleSelectionChange = useCallback(
    async (key: React.Key | null) => {
      if (!key) {
        return;
      }

      setPrediction(undefined);

      if (prediction && onAddressSelect) {
        onAddressSelect(prediction);
      }
    },
    [onAddressSelect, prediction],
  );

  const handleBlur = useCallback(() => {
    setPrediction(undefined);
    onBlur?.();
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    setIsAutocompleteEnabled(true);
    onFocus?.();
  }, [onFocus]);

  const inputElement = findChildOfType(children, PostalCode.Input);
  const optionsElement = findChildOfType(children, PostalCode.Options);

  return (
    <ComboBoxRAC
      inputValue={value ?? ''}
      onInputChange={handleInputChange}
      selectedKey={null}
      onSelectionChange={handleSelectionChange}
      isDisabled={disabled}
      isRequired={required}
      isInvalid={hasError}
      onBlur={handleBlur}
      onFocus={handleFocus}
      allowsCustomValue
      menuTrigger="input"
      items={options}
    >
      {applyInternalProps<InputInternalProps>(inputElement, {
        onFocus: handleFocus,
      })}
      {applyInternalProps<OptionsInternalProps>(optionsElement, {
        options,
      })}
    </ComboBoxRAC>
  );
};

type InputProps = {
  className?: string;
};

type InputInternalProps = {
  onFocus?: () => void;
};

const Input = ({ className, ...internal }: InputProps) => {
  const { id, placeholder } = useFieldPropsV2<MlaPostalCodeProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { onFocus } = internal as InputInternalProps;

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      placeholder={placeholder}
      onFocus={onFocus}
    />
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

type OptionsInternalProps = {
  options: { id: string; label: string }[];
};

const Options = ({ children, className }: OptionsProps) => {
  const optionElement = findChildOfType(children, Options.Option);

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC renderEmptyState={() => null}>
        {(item: { id: string; label: string }) =>
          applyInternalProps<OptionInternalProps>(optionElement, {
            id: item.id,
            label: item.label,
          })
        }
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type OptionProps = {
  className?: string;
};

type OptionInternalProps = {
  id: string;
  label: string;
};

const Option = ({ className, ...internal }: OptionProps) => {
  const { id, label } = internal as OptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={id} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Options.Option = Option;

PostalCode.Label = Label;
PostalCode.Input = Input;
PostalCode.Options = Options;
PostalCode.Description = Description;
PostalCode.Error = Error;
